// A text box that takes pasted or dropped images and files (and an attach button, for iPads), and offers @mentions.
// Images go into the text as Markdown, so they show where they were pasted; other files are listed under the text.
import { forwardRef, useImperativeHandle, useRef, useState, type ClipboardEvent, type DragEvent, type KeyboardEvent } from 'react';
import { ApiError } from '../api';
import { Icon, useToast } from '../ui';

export interface Uploaded { id: string; name: string; url: string; is_image: boolean; size: number }
export interface Mentionable { id: string; name: string; is_staff: boolean }

export async function uploadFile(url: string, file: File): Promise<Uploaded> {
  const form = new FormData();
  form.append('file', file, file.name || 'pasted-image.png');
  const res = await fetch(url, { method: 'POST', body: form, credentials: 'same-origin', headers: { 'X-BMP': '1' } });
  let data: unknown = null;
  try { data = await res.json(); } catch { /* empty */ }
  if (!res.ok) throw new ApiError(typeof (data as { detail?: unknown })?.detail === 'string' ? (data as { detail: string }).detail : 'Upload failed', res.status);
  return data as Uploaded;
}

const size = (n: number) => n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;

/** Files (not images) attached to a comment or ticket. */
export function AttachmentList({ files }: { files: Uploaded[] }) {
  if (!files.length) return null;
  return (
    <div className="att-list">
      {files.map((f) => <a key={f.id} className="att-file" href={f.url} download><Icon name="copy" className="ico xs" />{f.name}<span className="muted tiny">{size(f.size)}</span></a>)}
    </div>
  );
}

export interface RichTextareaHandle { focus: () => void; reset: (text?: string) => void }

interface Props {
  value: string;
  onChange: (text: string) => void;
  uploadUrl?: string;                         // no upload URL: no pasting files
  people?: Mentionable[];                     // offer @mentions from these
  onFiles?: (files: Uploaded[]) => void;      // non-image files attached so far
  onMentions?: (ids: string[]) => void;
  placeholder?: string;
  rows?: number;
  maxLength?: number;
  autoFocus?: boolean;
  className?: string;
  label?: string;
  onSubmit?: () => void;                      // Cmd/Ctrl+Enter
  onCancel?: () => void;                      // Escape
}

export const RichTextarea = forwardRef<RichTextareaHandle, Props>(function RichTextarea(
  { value, onChange, uploadUrl, people, onFiles, onMentions, placeholder, rows = 3, maxLength = 20000, autoFocus, className, label, onSubmit, onCancel }, ref) {
  const toast = useToast();
  const box = useRef<HTMLTextAreaElement>(null);
  const picker = useRef<HTMLInputElement>(null);
  const textRef = useRef(value);
  textRef.current = value;
  const [files, setFiles] = useState<Uploaded[]>([]);
  const [busy, setBusy] = useState(0);
  const [dragging, setDragging] = useState(false);
  const mentioned = useRef<Map<string, string>>(new Map());     // name -> id
  const [query, setQuery] = useState<{ text: string; start: number } | null>(null);
  const [pick, setPick] = useState(0);

  useImperativeHandle(ref, () => ({
    focus: () => box.current?.focus(),
    reset: (text = '') => { setFiles([]); onFiles?.([]); mentioned.current.clear(); onMentions?.([]); onChange(text); },
  }));

  const set = (text: string) => { textRef.current = text; onChange(text); };
  const insert = (snippet: string) => {
    const el = box.current, t = textRef.current;
    const start = el ? el.selectionStart : t.length, end = el ? el.selectionEnd : t.length;
    const before = t.slice(0, start), after = t.slice(end);
    const pad = before && !/\s$/.test(before) ? ' ' : '';
    set(before + pad + snippet + after);
    requestAnimationFrame(() => { if (el) { const p = (before + pad + snippet).length; el.focus(); el.setSelectionRange(p, p); } });
  };

  const upload = async (list: File[]) => {
    if (!uploadUrl || !list.length) return;
    for (const file of list) {
      const name = file.name && file.name !== 'image.png' ? file.name : `screenshot-${new Date().toISOString().slice(11, 19).replace(/:/g, '')}.png`;
      const marker = `![Uploading ${name}…]()`;
      const isImage = file.type.startsWith('image/');
      if (isImage) insert(marker);
      setBusy((n) => n + 1);
      try {
        const up = await uploadFile(uploadUrl, new File([file], name, { type: file.type }));
        if (up.is_image) set(textRef.current.replace(marker, `![${up.name}](${up.url})`));
        else {
          if (isImage) set(textRef.current.replace(marker, ''));
          setFiles((f) => { const next = [...f, up]; onFiles?.(next); return next; });
        }
      } catch (e) {
        if (isImage) set(textRef.current.replace(marker, ''));
        toast(`${name}: ${(e as Error).message}`, true);
      } finally { setBusy((n) => n - 1); }
    }
  };

  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const list = Array.from(e.clipboardData.files);
    if (list.length && uploadUrl) { e.preventDefault(); upload(list); }
  };
  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    setDragging(false);
    const list = Array.from(e.dataTransfer.files);
    if (list.length && uploadUrl) { e.preventDefault(); upload(list); }
  };

  // @mentions: a short list under the box while typing "@na…".
  const matches = query && people ? people.filter((p) => p.name.toLowerCase().includes(query.text.toLowerCase())).slice(0, 6) : [];
  const watch = (text: string, caret: number) => {
    if (!people?.length) return;
    const m = /(^|\s)@([^\s@][^@\n]{0,30}|)$/.exec(text.slice(0, caret));
    if (m && (m[2] === '' || people.some((p) => p.name.toLowerCase().includes(m[2].toLowerCase())))) { setQuery({ text: m[2], start: caret - m[2].length - 1 }); setPick(0); }
    else setQuery(null);
  };
  const choose = (p: Mentionable) => {
    if (!query) return;
    const t = textRef.current, caret = box.current?.selectionStart ?? t.length;
    const next = t.slice(0, query.start) + `@${p.name} ` + t.slice(caret);
    mentioned.current.set(p.name, p.id);
    onMentions?.([...mentioned.current.values()]);
    set(next);
    setQuery(null);
    requestAnimationFrame(() => { const el = box.current; if (el) { const pos = query.start + p.name.length + 2; el.focus(); el.setSelectionRange(pos, pos); } });
  };
  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (matches.length) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setPick((pick + 1) % matches.length); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setPick((pick - 1 + matches.length) % matches.length); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); choose(matches[pick]); return; }
      if (e.key === 'Escape') { e.preventDefault(); setQuery(null); return; }
    }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && onSubmit) { e.preventDefault(); if (!busy) onSubmit(); }
    if (e.key === 'Escape' && onCancel) onCancel();
  };

  return (
    <div className={`rich ${dragging ? 'dropping' : ''}`} onDragOver={(e) => { if (uploadUrl && e.dataTransfer.types.includes('Files')) { e.preventDefault(); setDragging(true); } }}
         onDragLeave={() => setDragging(false)} onDrop={onDrop}>
      <textarea ref={box} className={`input ${className ?? ''}`} rows={rows} value={value} maxLength={maxLength} placeholder={placeholder} autoFocus={autoFocus}
                aria-label={label ?? placeholder} onPaste={onPaste} onKeyDown={onKeyDown}
                onChange={(e) => { set(e.target.value); watch(e.target.value, e.target.selectionStart); }}
                onBlur={() => setTimeout(() => setQuery(null), 150)} />
      {matches.length > 0 && (
        <div className="mention-pop" role="listbox">
          {matches.map((p, i) => (
            <button key={p.id} type="button" role="option" aria-selected={i === pick} className={i === pick ? 'on' : ''} onMouseDown={(e) => { e.preventDefault(); choose(p); }}>
              {p.name}{p.is_staff ? <span className="muted tiny">Blue Market</span> : null}
            </button>
          ))}
        </div>
      )}
      {(uploadUrl || files.length > 0 || busy > 0) && (
        <div className="rich-foot">
          {uploadUrl && <>
            <button type="button" className="btn sm ghost" onClick={() => picker.current?.click()} title="Attach a file or photo (you can also paste or drop images)"><Icon name="plus" className="ico sm" />Attach</button>
            <input ref={picker} type="file" multiple hidden onChange={(e) => { upload(Array.from(e.target.files ?? [])); e.target.value = ''; }} />
          </>}
          {busy > 0 && <span className="muted tiny"><span className="spin" /> Uploading…</span>}
          {files.map((f) => (
            <span key={f.id} className="att-chip">{f.name}
              <button type="button" aria-label={`Remove ${f.name}`} onClick={() => setFiles((all) => { const next = all.filter((x) => x.id !== f.id); onFiles?.(next); return next; })}>×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
});
